import {
  useCallback, memo,
} from 'react';
import PropTypes from 'prop-types';
import DateFnsUtils from '@date-io/date-fns';
import dayjs from 'dayjs';
import dayjsPluginUTC from 'dayjs-plugin-utc';
import { MuiPickersUtilsProvider, DatePicker, DateTimePicker } from '@material-ui/pickers';
import { createMuiTheme, ThemeProvider } from '@material-ui/core';
import Dropdown from '../../Dropdown';
import Filter from '../Filter';

dayjs.extend(dayjsPluginUTC);

const materialTheme = createMuiTheme({
  typography: {
    fontFamily: 'var(--fontFamilyBody)',
  },
  overrides: {
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: 'var(--datePickersBgHex)',
      },
    },
    MuiPickersDay: {
      daySelected: {
        backgroundColor: 'var(--datePickersBgHex)!important',
      },
    },
    MuiPickerDTTabs: {
      tabs: {
        backgroundColor: 'var(--datePickersBgHex)',
      },
    },
    MuiPickersClockPointer: {
      pointer: {
        backgroundColor: 'var(--datePickersBgHex)',
      },
      thumb: {
        borderColor: 'var(--datePickersBgHex)',
      },
      noPoint: {
        backgroundColor: 'var(--datePickersBgHex)',
      },
    },
    MuiPickersClock: {
      pin: {
        backgroundColor: 'var(--datePickersBgHex)',
      },
    },
  },
});

const DateSelectorFilter = ({
  onChange,
  label,
  value,
  defaultValue,
  variant,
  filterButtonMaxWidth,
  ablePast,
  minDate,
  maxDate,
  format,
  widthInside,
  doOpenDatePicker, setDoOpenDatePicker,
  textColorClassName,
  bgClassName,
  leadDays,
}) => {
  const getFilterLabel = useCallback(
    () => (
      value
        ? `${label ? `${label}: ` : ''}${dayjs(value).format(format === 'datetime' ? 'MM/DD h:mm A' : 'ddd, MM/DD')}`
        : label
    ),
    [value, label, format],
  );

  const handleChange = useCallback((newValue) => {
    onChange(newValue);
  }, [onChange]);

  const clearFilter = useCallback(() => {
    onChange(defaultValue);
  }, [onChange, defaultValue]);

  const disableLeadDates = (date) => {
    const today = dayjs().startOf('day');
    return leadDays > 0 && dayjs(date).isBefore(today.add(leadDays, 'day'));
  };

  const firstValidDay = dayjs().add(leadDays + 1, 'day');

  return (
    <ThemeProvider theme={materialTheme}>
      <Filter
        label={getFilterLabel()}
        menuWidthClass="w-max"
        isFilterEmpty={value === defaultValue}
        variant={variant}
        filterMaxWidthClass={filterButtonMaxWidth}
        handleClear={clearFilter}
        widthInside={widthInside}
        doOpenDatePicker={doOpenDatePicker}
        setDoOpenDatePicker={setDoOpenDatePicker}
        bgClassName={bgClassName}
        textColorClassName={textColorClassName}
      >
        <Dropdown.Item>
          <div className="col-span-5">
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              {(() => {
                switch (format) {
                  case 'datetime':
                    return (
                      <DateTimePicker
                        autoOk
                        variant="static"
                        minutesStep={15}
                        openTo="date"
                        onChange={(date) => handleChange(dayjs(date).format('YYYY-MM-DDTHH:mm'))}
                        value={leadDays && !value ? firstValidDay : value}
                        shouldDisableDate={disableLeadDates}
                        disablePast
                        minDate={minDate}
                        maxDate={maxDate}
                      />
                    );
                  default:
                    return (
                      <DatePicker
                        autoOk
                        variant="static"
                        openTo="date"
                        onChange={(date) => handleChange(dayjs(date).local().format('YYYY-MM-DD'))}
                        value={leadDays && !value ? firstValidDay : dayjs(value ?? '').local()}
                        disablePast={!ablePast}
                        minDate={minDate}
                        maxDate={maxDate}
                        shouldDisableDate={disableLeadDates}
                        animateYearScrolling
                      />
                    );
                }
              })()}
            </MuiPickersUtilsProvider>
          </div>
        </Dropdown.Item>
      </Filter>
    </ThemeProvider>
  );
};

DateSelectorFilter.propTypes = {
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  variant: PropTypes.string,
  filterButtonMaxWidth: PropTypes.string,
  format: PropTypes.oneOf(['date', 'datetime']),
  ablePast: PropTypes.bool,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  maxDate: PropTypes.string,
  widthInside: PropTypes.string,
  doOpenDatePicker: PropTypes.bool,
  setDoOpenDatePicker: PropTypes.func,
  bgClassName: PropTypes.string,
  textColorClassName: PropTypes.string,
  leadDays: PropTypes.number,
};

DateSelectorFilter.defaultProps = {
  value: undefined,
  defaultValue: undefined,
  variant: 'standard',
  filterButtonMaxWidth: 'max-w-filter',
  format: 'date',
  ablePast: undefined,
  minDate: '1900-01-01',
  maxDate: '2100-01-01',
  widthInside: '',
  doOpenDatePicker: false,
  setDoOpenDatePicker: () => { },
  bgClassName: undefined,
  textColorClassName: undefined,
  leadDays: 0,
};

export default memo(DateSelectorFilter);
